"use client";

import React, { useState } from "react";
import { m, motion } from "framer-motion";

import {
  TextField,
  Button,
  Typography,
  Box,
  Paper,
  Grid,
  IconButton,
} from "@mui/material";
import {
  UploadFile as UploadIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  AccessTime as ClockIcon,
  LocationOn as MapPinIcon,
} from "@mui/icons-material";
import emailjs from "emailjs-com";
import { globalTransition } from "../../assets/animate/globalAnimation";

export default function ContactSection() {
  const [file, setFile] = useState<File | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    message: "",
    formType: "Contact Form", // Default value for formType
  });
  const [loading, setLoading] = useState(false);

  const [error, setError] = useState({
    name: "",
    phone: "",
    email: "",
    message: "",
  });

  const validateField = (name: string, value: string) => {
    switch (name) {
      case "name":
        return value.trim() ? "" : "Name is required";
      case "phone":
        return ""; // No specific validation for phone here
      case "email":
        return ""; // No specific validation for email here
      case "message":
        return ""; // Message is optional
      default:
        return "";
    }
  };

  const validateForm = () => {
    const errors: {
      name: string;
      phone: string;
      email: string;
      message: string;
    } = {
      name: "",
      phone: "",
      email: "",
      message: "",
    };

    if (!formData.name.trim()) {
      errors.name = "Name is required";
    }

    if (!formData.phone.trim() && !formData.email.trim()) {
      errors.phone = "Either phone or email is required";
      errors.email = "Either phone or email is required";
    } else if (formData.phone.trim() && !/^\d{10}$/.test(formData.phone)) {
      errors.phone = "Phone number must be 10 digits";
    } else if (
      formData.email.trim() &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)
    ) {
      errors.email = "Invalid email format";
    }

    setError(errors);

    // Ensure either phone or email is provided
    const isContactInfoProvided =
      formData.phone.trim() || formData.email.trim();

    // Return true if there are no errors and contact info is provided
    return (
      Object.values(errors).every((err) => err === "") && isContactInfoProvided
    );
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setFile(event.target.files[0]);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormData({
      ...formData,
      [name]: value,
    });

    // Validate the current field
    setError((prevErrors) => ({
      ...prevErrors,
      [name]: validateField(name, value),
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const isValid = validateForm();
    if (!isValid) {
      return;
    }

    setLoading(true); // Start the loader
    try {
      const formDataToSend = new FormData();
      formDataToSend.append("name", formData.name);
      formDataToSend.append("phone", formData.phone);
      formDataToSend.append("email", formData.email);
      formDataToSend.append("message", formData.message);
      formDataToSend.append("formType", formData.formType); // Include formType
      if (file) {
        formDataToSend.append("file", file);
      }

      const apiKey = process.env.REACT_APP_API_KEY_VALUE;
      const headers = {
        "x-api-key": apiKey as string,
       
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/send-contact-us`,
        {
          method: "POST",
          headers: headers,
          body: formDataToSend,
        }
      );

      if (response.ok) {
        alert("Form submitted successfully!");
        setFormData({
          name: "",
          phone: "",
          email: "",
          message: "",
          formType: "Contact Form", // Reset formType
        });
        setFile(null);
      } else {
        alert("Failed to submit the form");
      }
    } catch (error) {
      console.error("Error submitting the form:", error);
      alert("Error submitting the form");
    } finally {
      setLoading(false); // Stop the loader
    }
  };

  return (
    <div className="h-[72rem] md:h-[70rem] mdlg:h-[45rem] lg:h-[45rem] bg-[#3a4855] text-white mt-16 pt-16 !outoffice">
      {/* Career Section */}
      <section className="py-16 px-4 md:px-8 lg:px-16">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ ...globalTransition }}
          className="container mx-auto lg:text-center px-4 text-start"
        >
          <h2 className="lg:text-center text-start !text-4xl !md:text-5xl !font-normal mb-2 lg:mb-7">
            Contact Us
          </h2>

          <motion.div
            initial={{ x: 100, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ ...globalTransition }}
            className="max-w-4xl mx-auto mb-16"
          >
            <h2
              style={{ fontWeight: 300 }}
              className="lg:text-lg text-sm leading-relaxed text-white/80 py-4"
            >
              Have questions or need more information about our products?
              Request a quote or simply get in touch, and one of our on-site
              sales representatives will respond within 24 hours.
            </h2>
          </motion.div>

          <Grid container spacing={6} className="">
            {/* Form Section */}
            <Grid
              item
              xs={12}
              md={6}
              order={{ xs: 2, md: 1 }}
              className="h-full"
            >
              <motion.div
                initial={{ x: -100, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ ...globalTransition }}
              >
                <div className="flex justify-center lg:justify-end z-50">
                  <Paper
                    elevation={3}
                    className="p-6 md:p-8 text-start bg-white backdrop-blur-sm rounded-lg w-[30rem] xl:w-[25rem] min-h-[40rem] "
                    style={{ zIndex: 10 }} // Apply z-index here
                  >
                    <form
                      className="space-y-6 z-50 bg-white"
                      onSubmit={handleSubmit}
                    >
                      {/* Form fields */}
                      <div className="text-start">
                        <Typography
                          sx={{
                            fontWeight: "bold",
                            marginBottom: 1,
                            textAlign: "start",
                          }}
                          className="mb-2 font-sans"
                          variant="body1"
                        >
                          First & Last Name
                        </Typography>
                        <TextField
                          fullWidth
                          variant="outlined"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          InputProps={{
                            className: "bg-white/5 rounded-md text-white",
                          }}
                        />
                        <h2 className="text-red-400">{error?.name}</h2>
                      </div>

                      <div>
                        <Typography
                          sx={{
                            fontWeight: "bold",
                            marginBottom: 1,
                            textAlign: "start",
                          }}
                          variant="subtitle1"
                          className="mb-2"
                        >
                          Phone Number
                        </Typography>
                        <TextField
                          fullWidth
                          variant="outlined"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          InputProps={{
                            className: "bg-white/5 rounded-md text-white",
                          }}
                        />
                        <h2 className="text-red-400">{error?.phone}</h2>
                      </div>

                      <div>
                        <Typography
                          sx={{
                            fontWeight: "bold",
                            marginBottom: 1,
                            textAlign: "start",
                          }}
                          variant="subtitle1"
                          className="mb-2"
                        >
                          Email
                        </Typography>
                        <TextField
                          fullWidth
                          variant="outlined"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          InputProps={{
                            className: "bg-white/5 rounded-md text-white",
                          }}
                        />
                        <h2 className="text-red-400">{error?.email}</h2>
                      </div>

                      <div>
                        <Typography
                          sx={{
                            fontWeight: "bold",
                            marginBottom: 1,
                            textAlign: "start",
                          }}
                          variant="subtitle1"
                          className="mb-2"
                        >
                          Message
                        </Typography>
                        <TextField
                          fullWidth
                          multiline
                          rows={4}
                          placeholder="Type your message"
                          variant="outlined"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          InputProps={{
                            className: "bg-white/5 rounded-md text-white",
                          }}
                        />
                        <h2 className="text-red-400">{error?.message}</h2>
                      </div>

                      <Button
                        fullWidth
                        size="large"
                        type="submit"
                        className={`!bg-[#ab052d] !text-white mt-4 py-4 ${
                          loading ? "opacity-50 cursor-not-allowed" : ""
                        }`}
                        disabled={loading}
                      >
                        {loading ? "Submitting..." : "Send"}
                      </Button>
                    </form>
                  </Paper>
                </div>
              </motion.div>
            </Grid>

            {/* Contact Info Section */}
            <Grid item xs={12} md={6} order={{ xs: 1, md: 2 }}>
              <motion.div
                initial={{ x: 100, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ ...globalTransition }}
                className="h-full"
              >
                <Typography
                  variant="h4"
                  component="h2"
                  className="text-2xl md:text-3xl font-bold mb-8 text-start"
                >
                  Our Office
                </Typography>

                <div
                  className="space-y-4 text-white/80 text-start"
                  style={{ fontWeight: 300 }}
                >
                  <div className="flex items-start gap-4">
                    <MapPinIcon className="text-[#ab052d] h-6 w-6 mt-1 flex-shrink-0" />
                    <div>
                      <h2 className="text-lg">
                        L-4130/2, Phase IV, G.I.D.C
                        <br />
                        B/H New Nirma
                        <br />
                        Vatva, Ahmedabad-382445.
                      </h2>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <EmailIcon className="text-[#ab052d] h-6 w-6 mt-1 flex-shrink-0" />
                    <div className="space-y-1">
                      <h2 className="text-lg">
                        <a
                          href="mailto:sales@siskon.in"
                          className="hover:underline"
                          style={{ color: "inherit", textDecoration: "none" }}
                        >
                          sales@siskon.in
                        </a>
                      </h2>
                      <h2 className="text-lg">
                        <a
                          href="mailto:sanjay.jain@siskon.in"
                          className="hover:underline"
                          style={{ color: "inherit", textDecoration: "none" }}
                        >
                          sanjay.jain@siskon.in
                        </a>
                      </h2>
                      <h2 className="text-lg">
                        <a
                          href="mailto:ishan.jain@siskon.in"
                          className="hover:underline"
                          style={{ color: "inherit", textDecoration: "none" }}
                        >
                          ishan.jain@siskon.in
                        </a>
                      </h2>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <PhoneIcon className="text-[#ab052d] h-6 w-6 mt-1 flex-shrink-0" />
                    <div className="space-y-1">
                      <h2 className="text-lg">
                        <a
                          href="tel:+919898507238"
                          className="hover:underline"
                          style={{ color: "inherit", textDecoration: "none" }}
                        >
                          +91 9898507238
                        </a>
                      </h2>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <ClockIcon className="text-[#ab052d] h-6 w-6 mt-1 flex-shrink-0" />
                    <h2 className="text-lg">Mon – Sat: 9:00 AM – 6:00 PM</h2>
                  </div>
                </div>
              </motion.div>
            </Grid>
          </Grid>
        </motion.div>
      </section>
    </div>
  );
}
